import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PositionProfile } from 'src/app/models/position-profile.model';
import { Position } from 'src/app/models/position.model';
import { State } from 'src/app/models/state.model';
import { StateService } from 'src/app/services';
import { PositionProfileService } from 'src/app/services/position-profile.service';
import { CustomValidators } from 'src/app/shared/helpers';
import { AppState } from 'src/app/store/app.state';

@UntilDestroy()
@Component({
  selector: 'app-position-profile-item',
  templateUrl: './position-profile-item.component.html',
  styleUrl: './position-profile-item.component.scss'
})
export class PositionProfileItemComponent implements OnInit, OnChanges {

  @Input()
  public data: PositionProfile;

  @Input()
  public set position(value: Position) {
    this._position = value;
    this.updateFormState();
  }

  @Output()
  public validityChange = new EventEmitter<boolean>();


  private _position: Position;

  public get isSameCompany(): boolean {
    return this._position?.companyId === this.appState.company?.value?.id;
  }

  public get isKeyFactorVisible(): boolean {
    const code = this.data?.questionCategoryCode;
    return code && (code.startsWith('VIS') || code.startsWith('MOT'));
  }

  public state: State;
  public mainForm: UntypedFormGroup = null;

  constructor(
    private service: PositionProfileService,
    private fb: UntypedFormBuilder, 
    public stateService: StateService,
    private cd: ChangeDetectorRef,
    private appState: AppState
  ) { 
    this.state = this.stateService.state;
    this.mainForm = this.createEditForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['position']) {
      this.updateFormState();
    }

    
  }

  ngOnInit(): void {
    this.loadData();

    this.mainForm.valueChanges
    .pipe(untilDestroyed(this))
    .subscribe(() => {
      this.cd.detectChanges();
      this.validityChange.emit(this.mainForm.valid);
    });
  }

  private updateFormState(): void {
    if (this.mainForm) {
      if (this.state.isAssistant || !this.isSameCompany) {
        this.mainForm.get('factorMin')?.disable({ emitEvent: false });
        this.mainForm.get('factorMax')?.disable({ emitEvent: false });
        this.mainForm.get('isKeyFactor')?.disable({ emitEvent: false });
      } else {
        this.mainForm.get('factorMin')?.enable({ emitEvent: false });
        this.mainForm.get('factorMax')?.enable({ emitEvent: false });
        this.mainForm.get('isKeyFactor')?.enable({ emitEvent: false });
      }
    }
  }

  private createEditForm(): UntypedFormGroup {
      return this.fb.group({
        'id': 0,
        'factorMin': [{value: null, disabled: this.state.isAssistant || !this.isSameCompany} ], 
        'factorMax': [{value: null, disabled: this.state.isAssistant || !this.isSameCompany} ],
        'isKeyFactor': [{value: false, disabled: this.state.isAssistant || !this.isSameCompany} ],
      }, { validators: [
            CustomValidators.allOrNoneRequired(['factorMin', 'factorMax'], 'twoOrNoneRequired'),
            CustomValidators.firstLesserThanSecond('factorMin', 'factorMax', 'maximumLesserThanMinimumByOne', 1),
      ] });
  }

  private loadData(): void {
    if (this.data) {
      this.mainForm.patchValue(this.data);
    }
  }

  onBlur(event: any): void {
    this.mainForm.updateValueAndValidity();
    this.mainForm.get('factorMin').updateValueAndValidity();
    this.mainForm.get('factorMax').updateValueAndValidity();

    if (this.mainForm.invalid || this.mainForm.pristine) 
      return;
    
    let formData = this.mainForm.value as PositionProfile;
    formData.factorMin = formData.factorMin ? Number(formData.factorMin) : null;
    formData.factorMax = formData.factorMax ? Number(formData.factorMax) : null;

    let isDelete = !formData.factorMin && !formData.factorMax && this.data.id > 0;
    let isNew = formData.factorMin && formData.factorMin > 0 && formData.factorMax && formData.factorMax > 0 && this.data.id == 0;
    let isUpdate = formData.factorMin && formData.factorMin > 0 && formData.factorMax && formData.factorMax > 0 && this.data.id > 0;
    let isKeyFactorChange = this.data.isKeyFactor !== formData.isKeyFactor && this.data.id > 0;

    if (isDelete || isNew || isUpdate || isKeyFactorChange) {

      this.data.factorMin = formData.factorMin ? formData.factorMin : null;
      this.data.factorMax = formData.factorMax ? formData.factorMax : null;
      this.data.isKeyFactor = !!formData.isKeyFactor;

      this.service.save(this.data)
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        if (result.data)
          this.data.id = result.data.id;
        else {
          this.data.id = 0;
        }
      });
    }
  
  }

}
